import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import css from './WaitTable.css';
import TextHeader from './TextHeader';
import { urlParams } from 'utils/request';
import useStarfishAPI from '../hooks/useStarfishAPI';
import useError from '../hooks/useError';
import Icon from 'SF/MaterialIcon';
import { getWord } from 'utils/tenant';

function WaitTable(props) {

  // Declarative callbacks for the starfish API
  const [{ data, success, pending, error }, callAPI] = useStarfishAPI();

  useEffect(() => {
    const URL = `../kiosk/getStudentWaitingRoom.html`;
    callAPI(URL, {
      method: 'POST',
      body: urlParams({kioskId: props.kioskId})
    })
  }, [props.kioskId]);

  // Listen to error and call props.setError whenever there is a change
  useError(error, props.setError);

  if ( !success || pending ) return <div>Loading wait list...</div>;
  if ( !data.results || data.results.length < 1 ) return <div>No students found...</div>;

  const getStudents = () => data.results.find(({serviceId}) => serviceId === props.serviceId).students;

  const groupStudents = students => {
    const groupedStudents = [];
    const alreadyGrouped = [];
    students.forEach(student => {
      const { scheduleBlockId } = student;
      if ( scheduleBlockId === 0 || alreadyGrouped.indexOf(scheduleBlockId) === -1 ) {
        groupedStudents.push(student);
        alreadyGrouped.push(scheduleBlockId);
      }
    });
    return groupedStudents;
  };

  const getIcon = student => {
    if ( student.blockType === "GROUP_SESSION" ) return 'group'; // Group session
    if ( student.blockType === "DEFAULT" ) return 'event_available'; // Appointment
    return 'directions_walk'; // Walk in
  }

  const getType = student => {
    if ( student.blockType === "GROUP_SESSION" ) return 'Group';
    else if ( student.blockType === "DEFAULT" ) return 'Appointment';
    return getWord('dropIn', false);
  }

  const isCurrentUser = student => student.partyId === props.studentId;

  const getPosition = () => {
    const students = getStudents();
    const currentUser = students.find(isCurrentUser);
    const groupedStudents = groupStudents(students);
    return currentUser.scheduleBlockId === 0
      ? groupedStudents.findIndex(isCurrentUser) + 1
      : groupedStudents.findIndex(({scheduleBlockId}) => scheduleBlockId === currentUser.scheduleBlockId) + 1;
  };

  const getStudentText = (student) => {
    const students = getStudents();

    // If no group session, simply return student position or indicator of current user
    if ( student.scheduleBlockId === 0 ) return isCurrentUser(student) 
      ? "You" 
      : `Student ${students.findIndex(({partyId}) => partyId === student.partyId) + 1}`;

    const studentPositions = students
      .filter(({scheduleBlockId}) => scheduleBlockId === student.scheduleBlockId)
      .map(({partyId}) => ({partyId, position: students.findIndex(s => s.partyId === partyId) + 1}));

    // If current user is in the block
    const currentUser = studentPositions.find(s => s.partyId === props.studentId);
    if ( currentUser ) return studentPositions.length === 1
      ? "You"
      : `You and Student ${studentPositions.filter(({partyId}) => partyId !== props.studentId).map(({position}) => position).join(", ")}`;
     
    return `Student ${studentPositions.map(({position}) => position).join(", ")}`;
  };

  return (
    <>
      <TextHeader
        upperText={`You're number ${getPosition()} on the wait list. Someone will call you when ready.`}
      />
      <table className={css['table']}>
        <thead>
          <tr>
            <th>Student</th>
            <th>Type</th>
          </tr>
        </thead>
        <tbody>
          {groupStudents(getStudents()).map((student, i) => (
            <tr key={i} className={getPosition() === i + 1 ? css['selected'] : null}>
              <td>{getStudentText(student)}</td>
              <td><Icon icon={getIcon(student)} aria-hidden={true} /> {getType(student)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

WaitTable.propTypes = {
  kioskId: PropTypes.number.isRequired,
  setError: PropTypes.func.isRequired,
  serviceId: PropTypes.number.isRequired,
  studentId: PropTypes.number.isRequired,
};

export default WaitTable;
